import React, { useMemo } from 'react';
import { KeyboardDatePicker, KeyboardDatePickerProps, DatePicker } from '@material-ui/pickers';
import { ParsableDate } from '@material-ui/pickers/constants/prop-types';
import { useDateTimeFormatter, ThreeLetterDateFormats, isoDateToLocalDate } from '@utmost.co/utmost-design-system';
import { ReadOnly } from '../ReadOnly/ReadOnly';
import { useSmartFormContext } from '../../forms/utils/useSmartFormContext';
import { disabledAutoComplete, getAnonymousName } from '../utils/disableBrowserAutocomplete';
import { ComponentPropBase } from '../../types';
import { PermissionHelper } from '../common/PermissionHelper';
import { InfoText } from '../InfoText/InfoText';

type Props = Pick<
  KeyboardDatePickerProps,
  | 'disableFuture'
  | 'disablePast'
  | 'format'
  | 'helperText'
  | 'id'
  | 'maxDate'
  | 'maxDateMessage'
  | 'minDate'
  | 'minDateMessage'
  | 'onBlur'
  | 'onChange'
  | 'onFocus'
  | 'onError'
  | 'placeholder'
  | 'readOnly'
  | 'value'
> &
  Omit<ComponentPropBase, 'touched' | 'type'> & {
    dateFormat?: ThreeLetterDateFormats;
    error: any;
    keyboard?: boolean;
    showDateIcon?: boolean;
    inputDataTest?: string;
  };

export const DatePickerAtom: React.FC<React.PropsWithChildren<Props>> = (props) => {
  const { featureFlags, getDisabled, readonly, getFieldPermissions, permissionDebugger } = useSmartFormContext();
  const permissions = getFieldPermissions(props.name);

  const {
    dateFormat,
    disabled = getDisabled(props.name),
    keyboard = true,
    name,
    onError,
    showDateIcon = true,
    value,
    infoText,
    inputDataTest,
    ...restProps
  }: Props = props;

  const { dateTimeFormatter } = useDateTimeFormatter(dateFormat);

  const anonName = getAnonymousName(name);

  const commonProps = useMemo(
    () => ({
      autoOk: true,
      clearable: true,
      ...(featureFlags?.dateControlDisableScrollLock && { DialogProps: { disableScrollLock: true } }),
      disabled,
      FormHelperTextProps: {
        'data-test': `${name}-field-helper`,
        id: `${name}-helper-text`
      },
      fullWidth: true,
      id: anonName,
      InputLabelProps: {
        shrink: true
      },
      InputProps: {
        ...disabledAutoComplete
      },
      inputProps: {
        'data-test': inputDataTest
      },
      invalidLabel: '',
      name: anonName,
      onError: onError || (() => {}),
      value: isoDateToLocalDate(fixSmartFormDate(value))
    }),
    [anonName, value, disabled, name, onError, inputDataTest, featureFlags?.dateControlDisableScrollLock]
  );

  if (readonly || props.readOnly) {
    return (
      <ReadOnly
        permissionsId={props.id}
        permission={permissions}
        infoText={infoText}
        label={props.label}
        value={dateTimeFormatter(value as Date)}
      />
    );
  }

  if (keyboard) {
    return (
      <div>
        <KeyboardDatePicker
          keyboardIcon={showDateIcon ? undefined : null}
          KeyboardButtonProps={showDateIcon ? {} : { style: { display: 'none' } }}
          {...commonProps}
          {...restProps}
          minDate={restProps.minDate === null ? undefined : restProps.minDate}
          maxDate={restProps.maxDate === null ? undefined : restProps.maxDate}
          label={props.label && <InfoText label={props.label} required={props.required} tooltipText={infoText} />}
        />
        {permissionDebugger && <PermissionHelper permission={permissions} />}
      </div>
    );
  }

  return (
    <div>
      <DatePicker
        {...commonProps}
        {...restProps}
        minDate={restProps.minDate === null ? undefined : restProps.minDate}
        maxDate={restProps.maxDate === null ? undefined : restProps.maxDate}
      />
      {permissionDebugger && <PermissionHelper permission={permissions} />}
    </div>
  );
};

/**
 * Fix date if coming from a Smart Form configured initial date
 * using a slash instead of a dash e.g. 2022/10/06 => 2022-10-06
 */
function fixSmartFormDate(value?: ParsableDate) {
  return value?.toString().replace(/\//g, '-');
}
